import React, { useState } from 'react';
import axios from 'redaxios';
import ReactGA from 'react-ga';
import * as Sentry from '@sentry/browser';
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputRightAddon,
  VStack,
  Alert,
  AlertIcon,
  Text,
} from '@chakra-ui/react';
import ClockLoader from 'react-spinners/ClockLoader';
import { FaThumbsUp } from 'react-icons/fa';

import CaptureDetailsForm from '../capture-details-form';
import TrackingContext from '../../contexts/tracking';

const GACategory = 'newsletter';

const newsletterSignupFields = [
  {
    name: 'email',
    label: 'Email',
    required: true,
    placeholder: '',
    validate: (value) =>
      (typeof value !== 'string' || value.length < 3) &&
      'Email must be provided',
    createFormInput: ({
      onChange,
      onBlur,
      name,
      required,
      placeholder,
      validationErrors,
      isInvalid,
      isLoading,
      submitForm,
    }) => (
      <Box mt={2} mb={4}>
        <InputGroup>
          <Input
            type="email"
            onChange={onChange}
            onBlur={onBlur}
            name={name}
            required={required}
            placeholder={placeholder}
            maxLength={100}
            variant="outline"
            color="gray.900"
            bg="white"
            borderTopLeftRadius={4}
            borderBottomLeftRadius={4}
            isInvalid={isInvalid || validationErrors.length > 0}
            errorBorderColor="red.500"
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitForm();
              }
            }}
          />
          <InputRightAddon px={0} bg="transparent" border="none">
            <Button
              borderLeftRadius={0}
              colorScheme="orange"
              onClick={submitForm}
              disabled={isLoading || isInvalid}
              isLoading={isLoading}
              loadingText="Loading"
              spinner={<ClockLoader color="white" size={16} />}
            >
              sign me up
            </Button>
          </InputRightAddon>
        </InputGroup>
        {isInvalid && (
          <VStack>
            {validationErrors.map((error) => (
              <Alert key={error} my={2} status="error" color="gray.900">
                <AlertIcon /> {error}
              </Alert>
            ))}
          </VStack>
        )}
      </Box>
    ),
  },
];

const handleSubmit = async (formData, setSubmitted) => {
  try {
    await axios.post('/newsletter-signup', {
      data: {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        company: formData.company,
        trigger: 'newsletter signup',
      },
    });
    setSubmitted(true);
    ReactGA.event({
      category: GACategory,
      action: 'sign up',
    });
    return (
      <Box color="white" fontWeight="bold">
        <Text fontSize="3xl" pr={2}>
          <FaThumbsUp />
        </Text>
        Thanks, you&apos;ve been signed up
      </Box>
    );
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(
      'There was an error sending your message. Please try again later or email info@timelesstime.co.uk',
    );
  }
};

const NewsletterSignup = ({ ...props }) => {
  const [submitted, setSubmitted] = useState(false);
  return (
    <Box>
      {!submitted && (
        <Text>
          Monthly expert Management and HR content straight to your inbox.
        </Text>
      )}
      <TrackingContext.Consumer>
        {(tracking) => (
          <CaptureDetailsForm
            sx={{
              '.chakra-alert': {
                color: 'black',
              },
            }}
            fields={newsletterSignupFields}
            handleSubmit={(formData) => {
              tracking.associate(
                formData.email,
                formData.firstName,
                formData.lastName,
              );
              return handleSubmit(formData, setSubmitted);
            }}
            renderSubmitButton={false}
            showPersonalEmailWarning={false}
            {...props}
          />
        )}
      </TrackingContext.Consumer>
    </Box>
  );
};

export default NewsletterSignup;
